import { ref, type Ref } from 'vue';
import { first, isNil, last, toSafeInteger } from 'lodash-es';
import dayjs from 'dayjs';
import { controllerModel } from 'shared';
import type { flightEntity } from 'freedom-shared';

import {
  searchTicketsLib,
  type searchTicketsTypes,
} from '@/features/search-tickets';

type ComparatorFunction = (
  a: flightEntity.types.MappedOffer,
  b: flightEntity.types.MappedOffer,
) => number;

const getFirstDepTimestamp = (flights: flightEntity.types.MappedFlight[]) =>
  dayjs(first(first(flights)?.segments)?.dep.at).unix();

const getLastDepTimestamp = (flights: flightEntity.types.MappedFlight[]) =>
  dayjs(first(last(flights)?.segments)?.dep.at).unix();

const cheapestFirst: ComparatorFunction = (a, b) => {
  return toSafeInteger(a.price.amount) - toSafeInteger(b.price.amount);
};

const fastFirst: ComparatorFunction = (a, b) => {
  const aFirstFlightDuration = toSafeInteger(first(a.flights)?.duration);
  const bFirstFlightDuration = toSafeInteger(first(b.flights)?.duration);

  if (aFirstFlightDuration !== bFirstFlightDuration) {
    return aFirstFlightDuration - bFirstFlightDuration;
  }

  const aLastFlightDuration = toSafeInteger(last(a.flights)?.duration);
  const bLastFlightDuration = toSafeInteger(last(b.flights)?.duration);

  return aLastFlightDuration - bLastFlightDuration;
};

const earlyDepartureFirst: ComparatorFunction = (a, b) => {
  const aFirstDep = getFirstDepTimestamp(a.flights);
  const bFirstDep = getFirstDepTimestamp(b.flights);

  if (aFirstDep !== bFirstDep) {
    return aFirstDep - bFirstDep;
  }

  const aLastDep = getLastDepTimestamp(a.flights);
  const bLastDep = getLastDepTimestamp(b.flights);

  return aLastDep - bLastDep;
};

const lateDepartureFirst: ComparatorFunction = (a, b) => {
  const aFirstDep = getFirstDepTimestamp(a.flights);
  const bFirstDep = getFirstDepTimestamp(b.flights);

  if (aFirstDep !== bFirstDep) {
    return bFirstDep - aFirstDep;
  }

  const aLastDep = getLastDepTimestamp(a.flights);
  const bLastDep = getLastDepTimestamp(b.flights);

  return bLastDep - aLastDep;
};

const SORT_MODES = {
  RECOMMENDED: 'recommended',
  CHEAP: 'cheap',
  FAST: 'fast',
  EARLY_DEPARTURE: 'earlyDeparture',
  LATE_DEPARTURE: 'lateDeparture',
} as const;

export type SortMode = (typeof SORT_MODES)[keyof typeof SORT_MODES];

const NON_RECOMMENDED_SORTERS: Record<
  Exclude<SortMode, typeof SORT_MODES.RECOMMENDED>,
  ComparatorFunction | ComparatorFunction[]
> = {
  [SORT_MODES.CHEAP]: cheapestFirst,
  [SORT_MODES.FAST]: fastFirst,
  [SORT_MODES.EARLY_DEPARTURE]: earlyDepartureFirst,
  [SORT_MODES.LATE_DEPARTURE]: lateDepartureFirst,
};

type UseOfferSortOptions = {
  ranking?: Ref<searchTicketsTypes.OfferRankingEntry[] | undefined>;
};

export const DEFAULT_SORT_MODE = SORT_MODES.RECOMMENDED;

const combineComparators =
  (comparators: ComparatorFunction[]): ComparatorFunction =>
  (a: flightEntity.types.MappedOffer, b: flightEntity.types.MappedOffer) =>
    comparators.reduce(
      (result, compare) => (result === 0 ? compare(a, b) : result),
      0,
    );

function useOfferSort(options?: UseOfferSortOptions) {
  const sortMode = ref<SortMode>(SORT_MODES.RECOMMENDED);

  function changeSortMode(mode: SortMode) {
    sortMode.value = mode;
  }

  function sortOffers({
    offers,
  }: {
    offers: flightEntity.types.MappedOffer[];
  }) {
    if (isNil(sortMode.value)) {
      return offers;
    }

    if (sortMode.value === SORT_MODES.RECOMMENDED) {
      const compareFn: ComparatorFunction = (a, b) => {
        const ranking = options?.ranking?.value;
        if (ranking && ranking.length > 0) {
          return searchTicketsLib.compareGroupedOffersByRanking(a, b, ranking);
        }
        return searchTicketsLib.getWeight(b) - searchTicketsLib.getWeight(a);
      };
      return offers.sort(compareFn);
    }

    const sorter = NON_RECOMMENDED_SORTERS[sortMode.value];
    const compareFn = Array.isArray(sorter)
      ? combineComparators(sorter)
      : sorter;
    return offers.sort(compareFn);
  }

  return {
    sortMode,
    changeSortMode,
    sortOffers,
  };
}

const sortingBottomSheetController = () => {
  const bottomSheet = controllerModel.useBottomSheet();
  const sortingBottomSheetName = 'sorting-bottom-sheet-name';

  const show = () => bottomSheet.show(sortingBottomSheetName);
  const hide = () => bottomSheet.hide(sortingBottomSheetName);

  return { show, hide, name: sortingBottomSheetName };
};

export const offerSortModel = {
  useOfferSort,
  sortingBottomSheetController,
  SORT_MODES,
};

export { default as OfferSort } from './OfferSort.vue';
export { default as OfferSortButton } from './OfferSortButton.vue';
